import { ModalController, Platform } from '@ionic/angular';
import { EventEmitter, Injectable } from '@angular/core';
import { DISTANCE_FROM_TARGET, JoyrideBackdropService, JoyrideService } from 'ngx-joyride';
import { JoyrideOptions } from 'ngx-joyride/lib/models/joyride-options.class';
import { IInspectionOrder } from 'src/app/interfaces/Outputs';
import {
  EInspectionStep,
  EInspectionStepUrl,
} from 'src/app/interfaces/PWA/Inspection';
import { ITourStep } from 'src/app/interfaces/PWA/Tour';
import { StepManagerService } from '../StepManager/step-manager.service';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  interval,
  map,
  merge,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { OrientationService } from '../../General/Orientation/orientation.service';
import { RouteSharedDataService } from '../../Workarounds/RouteSharedData/route-shared-data.service';
import { PermissionsService } from '../../General/Permissions/permissions.service';
import { JoyrideStepInfo } from 'ngx-joyride/lib/models/joyride-step-info.class';

@Injectable({
  providedIn: 'root',
})
export class TourManagerService {

  constructor(
    private readonly joyrideService: JoyrideService,
    private readonly modalController: ModalController,
    private readonly stepManagerService: StepManagerService,
    private readonly permissionsService: PermissionsService,
    private readonly routeSharedDataService: RouteSharedDataService,
    private readonly orientationService: OrientationService,
    private readonly platform: Platform,
  ) {
      this.listenForOpenModals();
  }

  private listenForOpenModals() {
    interval(500)
      .pipe(
        switchMap(() => this.modalController.getTop()),
        distinctUntilChanged()
      )
      .subscribe((modal) => {
        const isActive = !!modal;
        if (isActive) {
          this.closeTour();
        }
      });
  }

  private async waitForNoModals(): Promise<void> {
    while (await this.modalController.getTop()) {
      await new Promise(resolve => setTimeout(resolve, 300)); // Esperar 300ms
    }
  }

  

  saveAppTooltipsTargets(inspectionOrder: IInspectionOrder) {
    const tooltips = inspectionOrder.tooltips;
    localStorage.setItem('tooltips', JSON.stringify(tooltips));
  }

  getAppTooltips(): ITourStep[] {
    const tooltips = localStorage.getItem('tooltips');
    if (!tooltips) return [];
    return JSON.parse(tooltips);
  }

  getStepTooltips(stepId: EInspectionStep): ITourStep[] {
    const tooltips = this.getAppTooltips();
    let targets = [] as string[];
    if (stepId === EInspectionStep.Forms) {
      targets = this.getFormPageTargets();
    }
    else if (stepId === EInspectionStep.Photo) {
      targets = this.getPhotoPageTargets();
    }
    return tooltips.filter((tooltip) => targets.includes(tooltip.Target) && document.querySelector(`[joyridestep="${tooltip.Target}"]`));
  }


  findTourStep(name: string): ITourStep | undefined {
    const tooltips = this.getAppTooltips();
    return tooltips.find((tooltip) => tooltip.Target === name);
  }


  closeTour() {
    this.joyrideService.closeTour();
  }


  getNotVisitedTooltips(tooltips: ITourStep[]): ITourStep[] {
    const visitedTargets = this.getVisitedTargets();
    const notVisitedTooltips = tooltips.filter(
      (tooltip) => !visitedTargets.includes(tooltip.Id)
    );
    return notVisitedTooltips;
  }

  getVisitedTargets(): number[] {
    const visitedTargets = localStorage.getItem('visitedTargets');
    if (!visitedTargets) return [];
    return JSON.parse(visitedTargets);
  }


  public async initStepTour(
    stepId: EInspectionStep,
    forcedTour: boolean = false
  ): Promise<void> {
    await this.waitForNoModals();
    const stepTooltips = this.getStepTooltips(stepId);
    const notVisitedTooltips = forcedTour ? stepTooltips : this.getNotVisitedTooltips(stepTooltips);
    const joyrideTour = this.startJoyrideTour(notVisitedTooltips);
    let previousStep = "";
    const joyrideTourSubscription = joyrideTour.subscribe({
      next: (stepInfo) => {
        // find the step that is being shown because the [joyride-step] property is the stepInfo.name
        if (previousStep) {
          const previousTarget = document.querySelector(`[joyridestep="${previousStep}"]`);
          previousTarget?.removeAttribute('style');
        }

        const target = document.querySelector(`[joyridestep="${stepInfo.name}"]`);
        target?.setAttribute('style', 'padding: 4px');
        previousStep = stepInfo.name;
      },
      complete: () => {
        if (previousStep) {
          const previousTarget = document.querySelector(`[joyridestep="${previousStep}"]`);
          previousTarget?.removeAttribute('style');
        }
        this.markVisitedTargets(notVisitedTooltips);
        joyrideTourSubscription.unsubscribe();
      },
    });
  }

  private startJoyrideTour(tooltips: ITourStep[]): Observable<JoyrideStepInfo> {
    const targets = this.getTourTargets(tooltips);
    const options = this.getTourOptions();
    const joyrideTour = this.joyrideService.startTour({
      steps: targets,
      ...options,
    });
    return joyrideTour;
  }

  private getTourTargets(tourSteps: ITourStep[]): string[] {
    return tourSteps.map((step) => {
      return step.Target;
    });
  }

  private getTourOptions(): Omit<JoyrideOptions, 'steps'> {
    return {
      themeColor: 'white',
      customTexts: {
        next: 'Siguiente >',
        prev: '< Anterior',
        done: 'OK',
        // close: 'Cerrar'
      },
    };
  }

  markVisitedTargets(tour: ITourStep[]) {
    const targetIds = tour.map((step) => step.Id);
    const visitedTargets = this.getVisitedTargets();
    const newTargetIds = targetIds.filter(id => !visitedTargets.includes(id));
    const newVisitedTargets = visitedTargets.concat(newTargetIds);
    localStorage.setItem('visitedTargets', JSON.stringify(newVisitedTargets));
  }

  getFormPageTargets(): string[] {
    return [
      'progress-bar',
      'timer',
      'help-desk',
      'form',
      'next-button',
      'prev-button',
    ];
  }

  getPhotoPageTargets(): string[] {
    return [
      'open-camera-button',
      'upload-file-button',
      'gallery',
      'notch'
    ]
  }

}
